import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Input,
  InputNumber,
  Row,
  Segmented,
  Select,
  Space,
  Tag,
  Typography,
  message,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import dayjs from 'dayjs';
import api from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import {
  ACCOUNT_TENURES,
  CALL_TYPE_OPTIONS,
  CONTACT_METHODS,
  RETAIN_IQ_OUTCOMES,
  RETAIN_IQ_TOPICS,
} from './constants';
import { NpsScorePicker } from './NpsScorePicker';
import { RetainIQCallLogTabBar } from './RetainIQCallLogTabBar';
import {
  useRetainIQCallLogTabs,
  type BusinessRowSnapshot,
  type CallLogPayload,
} from './RetainIQCallLogTabsContext';
import './retainiq.css';

const { Title, Text } = Typography;
const { TextArea } = Input;

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

type BusinessRow = BusinessRowSnapshot;

function formatBusinessAddress(b: Pick<BusinessRow, 'address' | 'city' | 'state' | 'zip_code'>): string {
  const parts = [b.address, b.city, b.state, b.zip_code].filter((x) => x && String(x).trim());
  return parts.length ? parts.join(', ') : '—';
}

export const RetainIQCallLog: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { activeTabId, activeDraft, updateActiveDraft, setActiveTabLabel, resetActiveDraft } = useRetainIQCallLogTabs();

  const {
    businessId,
    selectedBusiness,
    businessSearch,
    agentUserId,
    payload,
    createTicket,
    linkTicketInput,
    ticketSubject,
    ticketType,
    ticketCategoryId,
    ticketPriority,
    ticketComment,
  } = activeDraft;

  const [debouncedSearch, setDebouncedSearch] = useState(businessSearch);
  const [linkTicketPreview, setLinkTicketPreview] = useState<{ ticket_number: string; subject: string } | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(businessSearch), 300);
    return () => clearTimeout(t);
  }, [businessSearch]);

  useEffect(() => {
    setLinkTicketPreview(null);
  }, [activeTabId]);

  const { data: bizRes } = useQuery(
    ['retain-iq-businesses', debouncedSearch],
    () =>
      api
        .get(`/api/businesses/search?search=${encodeURIComponent(debouncedSearch)}&limit=200`)
        .then((r) => r.data.businesses || []),
    { staleTime: 30_000 }
  );
  const businesses = bizRes || [];

  const setPayload = (next: CallLogPayload | ((prev: CallLogPayload) => CallLogPayload)) => {
    updateActiveDraft((d) => ({
      ...d,
      payload: typeof next === 'function' ? next(d.payload) : next,
    }));
  };

  const clearBusinessSelection = () => {
    setActiveTabLabel('New call');
    updateActiveDraft((d) => ({
      ...d,
      businessId: undefined,
      selectedBusiness: null,
      businessSearch: '',
      payload: {
        ...d.payload,
        merchant_id: '',
        contact_name: '',
        contact_phone: '',
        contact_email: '',
        business_services: [],
      },
    }));
  };

  const applyBusinessFromRow = (b: BusinessRow) => {
    const display = b.business_name || b.name || 'Call log';
    setActiveTabLabel(display.length > 28 ? `${display.slice(0, 28)}…` : display);
    const mid = b.mid != null && String(b.mid).trim() ? String(b.mid).trim() : '';
    const contactName =
      b.contact_person != null && String(b.contact_person).trim() ? String(b.contact_person).trim() : '';
    const contactPhone =
      (b.cell_phone != null && String(b.cell_phone).trim() ? String(b.cell_phone).trim() : '') ||
      (b.phone != null && String(b.phone).trim() ? String(b.phone).trim() : '');
    const contactEmail = b.email != null && String(b.email).trim() ? String(b.email).trim() : '';
    const services = Array.isArray(b.services) ? b.services.filter((s) => s && String(s).trim()) : [];
    updateActiveDraft((d) => ({
      ...d,
      businessId: b.id,
      selectedBusiness: b,
      businessSearch: b.business_name || b.name || '',
      payload: {
        ...d.payload,
        merchant_id: mid || String(d.payload.merchant_id || ''),
        contact_name: contactName,
        contact_phone: contactPhone,
        contact_email: contactEmail,
        business_services: services,
      },
    }));
  };

  const businessOptions = useMemo(() => {
    const mapped = businesses.map((b: BusinessRow) => {
      const name = b.business_name || b.name || b.id;
      return { ...b, value: b.id, label: name };
    });
    if (
      selectedBusiness &&
      businessId &&
      !mapped.some((o: { value: string }) => o.value === businessId)
    ) {
      const name = selectedBusiness.business_name || selectedBusiness.name || selectedBusiness.id;
      mapped.unshift({ ...selectedBusiness, value: selectedBusiness.id, label: name });
    }
    return mapped;
  }, [businesses, selectedBusiness, businessId]);

  const { data: agentsRes } = useQuery('retain-iq-agents', () =>
    api.get('/api/retain-iq/agents').then((r) => r.data.agents || [])
  );
  const { data: settingsRes } = useQuery('retain-iq-settings', () =>
    api.get('/api/retain-iq/settings').then((r) => r.data)
  );

  const { data: categories = [] } = useQuery('retain-iq-categories', () =>
    api.get('/api/categories').then((r) => (Array.isArray(r.data?.categories) ? r.data.categories : []))
  );

  const { data: ticketTypes = [] } = useQuery('retain-iq-ticket-types', () =>
    api.get('/api/ticket-types').then((r) => (Array.isArray(r.data?.types) ? r.data.types : []))
  );

  useEffect(() => {
    if (!user?.id || !agentsRes?.length || agentUserId) return;
    const me = agentsRes.find((a: { id: string }) => String(a.id) === String(user.id));
    if (me) updateActiveDraft({ agentUserId: String(me.id) });
  }, [user?.id, agentsRes, agentUserId, updateActiveDraft]);

  useEffect(() => {
    const def = settingsRes?.default_ticket_category_id;
    if (def && !ticketCategoryId) {
      updateActiveDraft({ ticketCategoryId: String(def) });
    }
  }, [settingsRes, ticketCategoryId, updateActiveDraft]);

  const resolveLinkTicketPreview = async (raw: string) => {
    const trimmed = raw.trim().replace(/^#/, '');
    if (!trimmed || UUID_RE.test(trimmed)) {
      setLinkTicketPreview(null);
      return;
    }
    try {
      const res = await api.get(`/api/tickets/number/${encodeURIComponent(trimmed)}`);
      const t = res.data?.ticket ?? res.data;
      if (t?.ticket_number) {
        setLinkTicketPreview({ ticket_number: String(t.ticket_number), subject: String(t.subject || '') });
      } else setLinkTicketPreview(null);
    } catch {
      setLinkTicketPreview(null);
    }
  };

  const saveMutation = useMutation(
    async () => {
      if (!businessId) throw new Error('Select a business');
      if (!agentUserId) throw new Error('Select an agent');
      if (!String(payload.call_type || '').trim()) throw new Error('Call type is required');
      const body: Record<string, unknown> = {
        business_id: businessId,
        agent_user_id: agentUserId,
        payload,
      };
      const link = linkTicketInput.trim();
      if (link) {
        if (UUID_RE.test(link)) body.ticket_id = link;
        else body.ticket_number = link.replace(/^#/, '');
      }
      if (createTicket) {
        body.create_ticket = true;
        body.ticket = {
          subject: ticketSubject.trim() || undefined,
          type: ticketType,
          category_id: ticketCategoryId,
          priority: ticketPriority,
          comment: ticketComment.trim() || undefined,
        };
      }
      const res = await api.post('/api/retain-iq/logs', body);
      return res.data;
    },
    {
      onSuccess: () => {
        message.success('Call log saved');
        queryClient.invalidateQueries('retain-iq-reports');
        queryClient.invalidateQueries('retain-iq-logs');
        queryClient.invalidateQueries('retain-iq-follow-ups');
        resetActiveDraft();
        setLinkTicketPreview(null);
      },
      onError: (e: unknown) => {
        const err = e as { response?: { data?: { error?: string } } };
        message.error(err.response?.data?.error || 'Save failed');
      },
    }
  );

  const flatCallTypes = useMemo(
    () => CALL_TYPE_OPTIONS.flatMap((g) => g.options.map((o) => ({ label: `${g.label}: ${o.label}`, value: o.value }))),
    []
  );

  const toggleTopic = (topic: string, checked: boolean) => {
    const cur = (payload.topics as string[]) || [];
    if (checked) {
      setPayload({ ...payload, topics: [...cur, topic] });
    } else {
      const next: CallLogPayload = { ...payload, topics: cur.filter((t) => t !== topic) };
      if (topic === 'Other') next.topics_other = '';
      setPayload(next);
    }
  };

  const businessServices = (payload.business_services as string[]) || [];
  const topicsIncludeOther = ((payload.topics as string[]) || []).includes('Other');
  const nps = Number(payload.nps_score) || 0;

  return (
    <div>
      <RetainIQCallLogTabBar />
      <div className="retain-iq-no-print" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            Call log form
          </Title>
          <Text type="secondary">Drafts are saved per tab — switch away to find a ticket # without losing your work</Text>
        </div>
        <Space>
          <Button onClick={() => resetActiveDraft()}>Clear tab</Button>
          <Button onClick={() => window.print()}>Print</Button>
          <Button type="primary" loading={saveMutation.isLoading} onClick={() => saveMutation.mutate()}>
            Save call log
          </Button>
        </Space>
      </div>

      <div className="retain-iq-print-area">
        <Card title="Call information" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Text type="secondary">Date</Text>
              <DatePicker
                style={{ width: '100%', marginTop: 6 }}
                value={payload.call_date ? dayjs(String(payload.call_date)) : null}
                onChange={(d) => setPayload({ ...payload, call_date: d ? d.format('YYYY-MM-DD') : '' })}
              />
            </Col>
            <Col xs={24} md={8}>
              <Text type="secondary">Time</Text>
              <Input
                style={{ marginTop: 6 }}
                type="time"
                value={String(payload.call_time || '')}
                onChange={(e) => setPayload({ ...payload, call_time: e.target.value })}
              />
            </Col>
            <Col xs={24} md={8}>
              <Text type="secondary">Duration (min)</Text>
              <InputNumber
                style={{ width: '100%', marginTop: 6 }}
                min={0}
                value={payload.call_duration === '' ? undefined : Number(payload.call_duration)}
                onChange={(v) => setPayload({ ...payload, call_duration: v == null ? '' : String(v) })}
              />
            </Col>
            <Col xs={24} md={8} style={{ marginTop: 16 }}>
              <Text type="secondary">Agent</Text>
              <Select
                showSearch
                optionFilterProp="label"
                style={{ width: '100%', marginTop: 6 }}
                placeholder="Agent"
                value={agentUserId}
                onChange={(v) => updateActiveDraft({ agentUserId: v })}
                options={(agentsRes || []).map((a: { id: string; name: string; level_label?: string }) => ({
                  value: a.id,
                  label: `${a.name}${a.level_label ? ` — ${a.level_label}` : ''}`,
                }))}
              />
            </Col>
            <Col xs={24} md={8} style={{ marginTop: 16 }}>
              <Text type="secondary">Call type</Text>
              <Select
                showSearch
                style={{ width: '100%', marginTop: 6 }}
                placeholder="Select call type"
                value={payload.call_type || undefined}
                onChange={(v) => setPayload({ ...payload, call_type: v })}
                options={flatCallTypes}
              />
            </Col>
            <Col xs={24} md={8} style={{ marginTop: 16 }}>
              <Text type="secondary">Contact method</Text>
              <Select
                style={{ width: '100%', marginTop: 6 }}
                value={String(payload.contact_method || 'Phone Call')}
                onChange={(v) => setPayload({ ...payload, contact_method: v })}
                options={CONTACT_METHODS.map((c) => ({ value: c, label: c }))}
              />
            </Col>
            <Col span={24} style={{ marginTop: 16 }}>
              <Text type="secondary">Contact made?</Text>
              <div style={{ marginTop: 8 }}>
                <Segmented
                  value={String(payload.contact_made || 'Reached')}
                  onChange={(v) => setPayload({ ...payload, contact_made: String(v) })}
                  options={[
                    { label: 'Reached', value: 'Reached' },
                    { label: 'Voicemail', value: 'Voicemail' },
                    { label: 'No answer', value: 'No Answer' },
                  ]}
                />
              </div>
            </Col>
          </Row>
        </Card>

        <Card title="Account details" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Text type="secondary">Business</Text>
              <Select
                showSearch
                allowClear
                filterOption={false}
                virtual={false}
                popupMatchSelectWidth={false}
                dropdownStyle={{ minWidth: 380 }}
                onSearch={(v) => updateActiveDraft({ businessSearch: v })}
                onOpenChange={(open) => {
                  if (open) updateActiveDraft({ businessSearch: '' });
                }}
                style={{ width: '100%', marginTop: 6 }}
                placeholder="Search businesses"
                value={businessId}
                onChange={(id: string | undefined) => {
                  if (!id) {
                    clearBusinessSelection();
                    return;
                  }
                  const b = businesses.find((x: BusinessRow) => x.id === id) || (selectedBusiness?.id === id ? selectedBusiness : null);
                  if (b) applyBusinessFromRow(b);
                  else updateActiveDraft({ businessId: id });
                }}
                onClear={clearBusinessSelection}
                optionRender={(ori) => {
                  const d = ori.data as BusinessRow & { label?: string };
                  const sid = d.store_id != null && String(d.store_id).trim() ? String(d.store_id).trim() : '—';
                  const mid = d.mid != null && String(d.mid).trim() ? String(d.mid).trim() : '—';
                  const addr = formatBusinessAddress(d);
                  return (
                    <div style={{ lineHeight: 1.35, padding: '2px 0' }}>
                      <div style={{ fontWeight: 500 }}>{d.label || d.business_name || d.name || d.id}</div>
                      <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.55)' }}>
                        Store ID: {sid} · MID: {mid}
                      </div>
                      <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.55)' }}>{addr}</div>
                    </div>
                  );
                }}
                options={businessOptions}
              />
            </Col>
            <Col xs={24} md={12}>
              <Text type="secondary">Merchant ID / Account #</Text>
              <Input
                style={{ marginTop: 6 }}
                value={String(payload.merchant_id || '')}
                onChange={(e) => setPayload({ ...payload, merchant_id: e.target.value })}
              />
            </Col>
            <Col xs={24} md={12} style={{ marginTop: 12 }}>
              <Text type="secondary">Contact name</Text>
              <Input
                style={{ marginTop: 6 }}
                value={String(payload.contact_name || '')}
                onChange={(e) => setPayload({ ...payload, contact_name: e.target.value })}
              />
            </Col>
            <Col xs={24} md={12} style={{ marginTop: 12 }}>
              <Text type="secondary">Phone</Text>
              <Input
                style={{ marginTop: 6 }}
                value={String(payload.contact_phone || '')}
                onChange={(e) => setPayload({ ...payload, contact_phone: e.target.value })}
              />
            </Col>
            <Col xs={24} md={12} style={{ marginTop: 12 }}>
              <Text type="secondary">Email</Text>
              <Input
                style={{ marginTop: 6 }}
                value={String(payload.contact_email || '')}
                onChange={(e) => setPayload({ ...payload, contact_email: e.target.value })}
              />
            </Col>
            <Col xs={24} md={12} style={{ marginTop: 12 }}>
              <Text type="secondary">Services</Text>
              <div style={{ marginTop: 8, minHeight: 32 }}>
                {businessServices.length > 0 ? (
                  businessServices.map((s) => (
                    <Tag key={s} style={{ marginBottom: 4 }}>
                      {s}
                    </Tag>
                  ))
                ) : (
                  <Text type="secondary">Select a business to load services</Text>
                )}
              </div>
            </Col>
            <Col xs={24} md={12} style={{ marginTop: 12 }}>
              <Text type="secondary">Account tenure</Text>
              <Select
                allowClear
                style={{ width: '100%', marginTop: 6 }}
                value={payload.account_tenure || undefined}
                onChange={(v) => setPayload({ ...payload, account_tenure: v || '' })}
                options={ACCOUNT_TENURES.filter(Boolean).map((t) => ({ value: t, label: t }))}
              />
            </Col>
            <Col span={24} style={{ marginTop: 16 }}>
              <Text type="secondary">Risk level</Text>
              <div style={{ marginTop: 8 }}>
                <Segmented
                  value={payload.risk_level ? String(payload.risk_level) : undefined}
                  onChange={(v) => setPayload({ ...payload, risk_level: v ? String(v) : '' })}
                  options={[
                    { label: 'Low', value: 'low' },
                    { label: 'Medium', value: 'medium' },
                    { label: 'High', value: 'high' },
                    { label: 'Critical', value: 'critical' },
                  ]}
                />
              </div>
            </Col>
          </Row>
        </Card>

        <Card title="Discussion summary" style={{ marginBottom: 16 }}>
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <div>
              <Text type="secondary">Topics discussed</Text>
              <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {RETAIN_IQ_TOPICS.map((topic) => (
                  <Checkbox
                    key={topic}
                    checked={((payload.topics as string[]) || []).includes(topic)}
                    onChange={(e) => toggleTopic(topic, e.target.checked)}
                  >
                    {topic}
                  </Checkbox>
                ))}
              </div>
              {topicsIncludeOther && (
                <TextArea
                  style={{ marginTop: 8 }}
                  rows={2}
                  placeholder="Describe other topics"
                  value={String(payload.topics_other || '')}
                  onChange={(e) => setPayload({ ...payload, topics_other: e.target.value })}
                />
              )}
            </div>
            <div>
              <Text type="secondary">Customer sentiment</Text>
              <div style={{ marginTop: 8 }}>
                <Segmented
                  value={payload.sentiment ? String(payload.sentiment) : undefined}
                  onChange={(v) => setPayload({ ...payload, sentiment: v ? String(v) : '' })}
                  options={[
                    { label: 'Very +', value: 'Very Positive' },
                    { label: 'Positive', value: 'Positive' },
                    { label: 'Neutral', value: 'Neutral' },
                    { label: 'Negative', value: 'Negative' },
                    { label: 'Very −', value: 'Very Negative' },
                  ]}
                />
              </div>
            </div>
            <Row gutter={16}>
              <Col span={24} md={12}>
                <Text type="secondary">Pain points / issues</Text>
                <TextArea
                  style={{ marginTop: 6 }}
                  rows={3}
                  value={String(payload.pain_points || '')}
                  onChange={(e) => setPayload({ ...payload, pain_points: e.target.value })}
                />
              </Col>
              <Col span={24} md={12}>
                <Text type="secondary">Key wins / positive feedback</Text>
                <TextArea
                  style={{ marginTop: 6 }}
                  rows={3}
                  value={String(payload.positive_notes || '')}
                  onChange={(e) => setPayload({ ...payload, positive_notes: e.target.value })}
                />
              </Col>
            </Row>
            <div>
              <Text type="secondary">Commitments made</Text>
              <TextArea
                style={{ marginTop: 6 }}
                rows={2}
                value={String(payload.commitments || '')}
                onChange={(e) => setPayload({ ...payload, commitments: e.target.value })}
              />
            </div>
          </Space>
        </Card>

        <Card title="Follow-up & outcome" style={{ marginBottom: 16 }}>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Text type="secondary">Follow-up required?</Text>
              <div style={{ marginTop: 8 }}>
                <Segmented
                  value={String(payload.followup_flag || 'No')}
                  onChange={(v) => setPayload({ ...payload, followup_flag: String(v) })}
                  options={[
                    { label: 'No', value: 'No' },
                    { label: 'Yes', value: 'Yes' },
                  ]}
                />
              </div>
            </Col>
            {payload.followup_flag === 'Yes' && (
              <>
                <Col xs={24} md={12}>
                  <Text type="secondary">Follow-up date</Text>
                  <DatePicker
                    style={{ width: '100%', marginTop: 6 }}
                    value={payload.followup_date ? dayjs(String(payload.followup_date)) : null}
                    onChange={(d) => setPayload({ ...payload, followup_date: d ? d.format('YYYY-MM-DD') : '' })}
                  />
                </Col>
                <Col xs={24} md={12}>
                  <Text type="secondary">Follow-up action / owner</Text>
                  <Input
                    style={{ marginTop: 6 }}
                    value={String(payload.followup_action || '')}
                    onChange={(e) => setPayload({ ...payload, followup_action: e.target.value })}
                  />
                </Col>
              </>
            )}
            <Col span={24}>
              <Text type="secondary">Overall call outcome</Text>
              <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {RETAIN_IQ_OUTCOMES.map((o) => (
                  <Button
                    key={o}
                    type={payload.call_outcome === o ? 'primary' : 'default'}
                    size="small"
                    onClick={() => setPayload({ ...payload, call_outcome: o })}
                  >
                    {o}
                  </Button>
                ))}
              </div>
            </Col>
            <Col span={24}>
              <Text type="secondary">How likely are they to recommend? (NPS 0–10)</Text>
              <div style={{ marginTop: 8 }}>
                <NpsScorePicker value={nps} onChange={(v) => setPayload({ ...payload, nps_score: v })} />
              </div>
            </Col>
            <Col span={24}>
              <Text type="secondary">Additional notes</Text>
              <TextArea
                style={{ marginTop: 6 }}
                rows={4}
                value={String(payload.call_notes || '')}
                onChange={(e) => setPayload({ ...payload, call_notes: e.target.value })}
              />
            </Col>
          </Row>
        </Card>

        <Card title="Ticket (optional)" style={{ marginBottom: 16 }} className="retain-iq-no-print">
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <div>
              <Text type="secondary">Link existing ticket (# or ID)</Text>
              <Input
                style={{ marginTop: 6, maxWidth: 400 }}
                placeholder="Ticket # (e.g. 12345) or UUID"
                value={linkTicketInput}
                onChange={(e) => {
                  updateActiveDraft({ linkTicketInput: e.target.value });
                  setLinkTicketPreview(null);
                }}
                onBlur={(e) => resolveLinkTicketPreview(e.target.value)}
                disabled={createTicket}
              />
              {linkTicketPreview && (
                <Text type="secondary" style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
                  Found #{linkTicketPreview.ticket_number}: {linkTicketPreview.subject}
                </Text>
              )}
            </div>
            <Checkbox
              checked={createTicket}
              onChange={(e) => updateActiveDraft({ createTicket: e.target.checked })}
              disabled={!!linkTicketInput.trim()}
            >
              Create support ticket (assigned to call agent)
            </Checkbox>
            {createTicket && (
              <Row gutter={16} style={{ width: '100%' }}>
                <Col xs={24} md={12}>
                  <Text type="secondary">Category</Text>
                  <Select
                    showSearch
                    optionFilterProp="children"
                    style={{ width: '100%', marginTop: 6 }}
                    placeholder="Category"
                    value={ticketCategoryId}
                    onChange={(v) => updateActiveDraft({ ticketCategoryId: v })}
                    options={categories.map((c: { id: string; name: string }) => ({ value: c.id, label: c.name }))}
                  />
                </Col>
                <Col xs={24} md={12}>
                  <Text type="secondary">Type</Text>
                  <Select
                    showSearch
                    style={{ width: '100%', marginTop: 6 }}
                    placeholder="Ticket type"
                    value={ticketType}
                    onChange={(v) => updateActiveDraft({ ticketType: v })}
                    options={ticketTypes
                      .filter((t: { is_active?: boolean }) => t.is_active !== false)
                      .map((t: { id: string; name: string }) => ({ value: t.name, label: t.name }))}
                  />
                </Col>
                <Col xs={24} md={12}>
                  <Text type="secondary">Priority</Text>
                  <Select
                    style={{ width: '100%', marginTop: 6 }}
                    value={ticketPriority}
                    onChange={(v) => updateActiveDraft({ ticketPriority: v })}
                    options={[
                      { value: 'low', label: 'Low' },
                      { value: 'medium', label: 'Medium' },
                      { value: 'high', label: 'High' },
                    ]}
                  />
                </Col>
                <Col span={24}>
                  <Text type="secondary">Ticket comment</Text>
                  <TextArea
                    style={{ marginTop: 6 }}
                    rows={3}
                    placeholder="Optional note for the support ticket"
                    value={ticketComment}
                    onChange={(e) => updateActiveDraft({ ticketComment: e.target.value })}
                  />
                </Col>
                <Col span={24}>
                  <Text type="secondary">Subject override</Text>
                  <Input
                    style={{ marginTop: 6 }}
                    placeholder="Defaults from call type + business"
                    value={ticketSubject}
                    onChange={(e) => updateActiveDraft({ ticketSubject: e.target.value })}
                  />
                </Col>
              </Row>
            )}
          </Space>
        </Card>
      </div>
    </div>
  );
};
